import { FC, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { LchColorWheel } from 'lch-color-wheel';
import { PropsWithClassName } from '@/types/PropsWithClassName';
import { cn } from '@/utils/classNames.ts';
import { lch, Oklch, oklch } from 'culori';
import useResizeObserver from 'use-resize-observer';

type ColorPickerProps = {
  onChange?: (color: Oklch) => void;
  currentColor?: Oklch;
};
const ColorPicker: FC<PropsWithClassName<ColorPickerProps>> = ({ onChange, currentColor, className }) => {
  const WheelInstance = useRef<InstanceType<typeof LchColorWheel>>();
  const domElementRef = useRef<HTMLDivElement>(null);
  const { ref, width } = useResizeObserver<HTMLDivElement>();
  const [color, setColor] = useState<Oklch | undefined>(currentColor);

  const handleOnChange = useCallback((colorWheel: InstanceType<typeof LchColorWheel>) => {
    const lchColor = lch({ mode: 'lch', l: colorWheel.lch[0], c: colorWheel.lch[1], h: colorWheel.lch[2] });
    const oklchColor = oklch(lchColor);

    if (!oklchColor) return;

    setColor(oklchColor);

    if (onChange) {
      onChange(oklchColor);
    }
  }, []);

  useLayoutEffect(() => {
    if (WheelInstance.current || !domElementRef.current) return;
    WheelInstance.current = new LchColorWheel({
      appendTo: domElementRef.current!,
      onChange: handleOnChange,
      wheelDiameter: width,
    });
  }, []);

  useLayoutEffect(() => {
    if (!WheelInstance.current || !width) return;
    WheelInstance.current.wheelDiameter = width;
    WheelInstance.current.redraw();
  }, [width]);

  return (
    <div ref={ref} className={cn('relative', className)}>
      <div
        className="w-6 sm:w-10 h-6 sm:h-10 rounded-full absolute"
        style={{
          backgroundColor: `oklch(${color?.l} ${color?.c} ${color?.h})`,
        }}
      ></div>
      <div className="w-full" ref={domElementRef}></div>
    </div>
  );
};

export default ColorPicker;
